import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Alert, Button } from '@mui/material'
import { Snackbar } from '@mui/material'
import * as Yup from 'yup'
import { useFormik } from 'formik'

import { contractApi } from '../../../api/contract'
import { CustomInput } from '../../../components/FormComponets'
import Modal from '../../../components/Modal'
import LoadingOverlay from '../../../components/LoadingOverlay'
import './index.scss'
import { ContextEntity } from '../../../customEntites/context.entity'
import { GlobalContext } from '../../../App'
import { generateError } from '../../../services/error.service'
import { ErrorSummary } from '../../../components/ErrorSummary'
import { getData, removeData, saveData } from '../../../services/dataPreserve.service'

export const AddContract = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [manualApprovalSuccessModal, setManualApprovalSuccessModal] = useState(false)
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false)
  const [showErrorAlert, setShowErrorAlert] = useState(false)
  const [errorAlertMsg, setErrorAlertMsg] = useState('')
  const [submitButtonState, setSubmitButtonState] = useState('')
  const { context, setContext }: ContextEntity = useContext(GlobalContext)

  const fieldRefs = {
    contractNumber: useRef<HTMLInputElement>(null),
    zip: useRef<HTMLInputElement>(null),
    city: useRef<HTMLInputElement>(null),
    street: useRef<HTMLInputElement>(null),
    streetNumber: useRef<HTMLInputElement>(null)
  };

  useEffect(() => {
    (async () => {
      setShowLoadingOverlay(true)
      if (context?.contracts?.contracts?.length === 0) {
        await contractApi.getContractsByAppId()
          .then(res => {
            const activeContracts = res.filter((contract) => contract.approvalState == 'approved')
            setContext((prev: any) => ({ ...prev, contracts: { activeContracts, contracts: res } }))
            setShowErrorAlert(false)
          })
          .catch(err => {
            setShowErrorAlert(true)
            setErrorAlertMsg(generateError(err?.response?.data?.message))
          })
      }
      setShowLoadingOverlay(false)
    })()
  }, [])


  const handleSubmitButtonState = (isSubmit: string) => {
    setSubmitButtonState(isSubmit)
  }

  const AddContractSchema = Yup.object().shape({
    contractNumber: Yup.string().matches(/^([0-9]{4})(\/)\w+$/, `${t('FORM.INVALID_CONTRACT_NUMBER')}`).required(`${t('FORM.CONTRACT_NUMBER_REQUIRED')}`),
    zip: Yup.string().required(`${t('FORM.ZIP_REQUIRED')}`),
    city: Yup.string().required(`${t('FORM.CITY_REQUIRED')}`),
    street: Yup.string().required(`${t('FORM.STREET_REQUIRED')}`),
    streetNumber: Yup.string().required(`${t('FORM.STREET_NUMBER_REQUIRED')}`)
  })

  const formik = useFormik({
    initialValues: {
      contractNumber: '',
      zip: '',
      city: '',
      street: '',
      streetNumber: ''
    },
    onSubmit: async () => {
      setShowLoadingOverlay(true)
      const contracts = await contractApi.getContractsByAppId()
      const existedContract = contracts?.find((contract) => (contract.contractNumber.replace('-', '/') === formik.values.contractNumber && contract.approvalState !== 'denied'))
      if (existedContract) {
        setErrorAlertMsg(`${t('ADD_CONTRACT.CONTRACT_EXIST')}`)
        setShowLoadingOverlay(false)
        setShowErrorAlert(true)
        return
      }
      if (submitButtonState === 'primary') {
        (async () => {
          await contractApi.addContract(
            {
              contractNumber: formik.values.contractNumber,
              address: `${formik.values.street} ${formik.values.streetNumber}, ${formik.values.zip} ${formik.values.city}`
            }
          )
            .then(() => {
              setShowErrorAlert(false)
              setShowSuccessModal(true)
            })
            .catch((err) => {
              if (err?.status === 401) {
                saveData('contract', {
                  contractNumber: formik.values.contractNumber,
                  street: formik.values.street,
                  streetNumber: formik.values.streetNumber,
                  zip: formik.values.zip,
                  city: formik.values.city
                })
              }
              setErrorAlertMsg(generateError(err?.response?.data?.message))
              setShowErrorAlert(true)
            })
          setShowLoadingOverlay(false)
        })()

      } else if (submitButtonState === 'secondary') {
        (async () => {
          await contractApi.manualApproval(
            {
              contractNumber: formik.values.contractNumber,
              address: `${formik.values.street} ${formik.values.streetNumber}, ${formik.values.zip} ${formik.values.city}`
            }
          )
            .then(() => {
              setManualApprovalSuccessModal(true)
              setShowErrorAlert(false)
            })
            .catch((err) => {
              if (err?.status === 401) {
                saveData('contract', {
                  contractNumber: formik.values.contractNumber,
                  street: formik.values.street,
                  streetNumber: formik.values.streetNumber,
                  zip: formik.values.zip,
                  city: formik.values.city
                })
              }
              setErrorAlertMsg(generateError(err?.response?.data?.message))
              setShowErrorAlert(true)
            })
          setShowLoadingOverlay(false)
        })()
      }
    },
    validationSchema: AddContractSchema
  })

  useEffect(() => {
    if (formik.isSubmitting) {
      setSubmitted(true)
    }
  }, [formik])

  useEffect(() => {
    const data = getData('contract')
    if (data) {
      formik.setValues({
        contractNumber: data.contractNumber,
        street: data.street,
        streetNumber: data.streetNumber,
        zip: data.zip,
        city: data.city
      })
      removeData('contract')
    }
  }, [])

  const onSuccessModalCancel = () => {
    setShowSuccessModal(false)
    navigate('/contracts')
  }

  return (
    <div className='add-contract-page-wrapper'>
      <ErrorSummary errors={formik.errors} submitted={submitted} fieldRefs={fieldRefs} errorMessageOrder={['contractNumber', 'zip', 'city', 'street', 'streetNumber']} />
      {showLoadingOverlay && (
        <LoadingOverlay />
      )}
      {showSuccessModal && (
        <Modal
          head={`${t('ADD_CONTRACT.THANK_YOU')}`}
          text={`${t('ADD_CONTRACT.SUCCESSFULLY_ADDED')}`}
          cancelText={`${t('ADD_CONTRACT.BACK_TO_CONTRACTS')}`}
          onCancel={() => onSuccessModalCancel()}
          onConfirm={null}
          confirmText={null}
        />
      )}
      {manualApprovalSuccessModal && (
        <Modal
          head={`${t('ADD_CONTRACT.THANK_YOU')}`}
          text={`${t('ADD_CONTRACT.SUCCESSFULLY_MANUAL_ADDED')}`}
          cancelText={null}
          onCancel={() => onSuccessModalCancel()}
          onConfirm={null}
          confirmText={null}
        />
      )}
      <div className='subpage-head-content'>
        <h3 role='heading' id='add-contract-form'>{t('ADD_CONTRACT.ADD_CONTRACT')}</h3>
        <p>{t('ADD_CONTRACT.ENTER_CONTRACT_DETAILS')}</p>
      </div>
      <form className='form-center' aria-labelledby='add-contract-form' role='form'>
        <CustomInput
          inputRef={fieldRefs.contractNumber}
          label={`${t('ADD_CONTRACT.CONTRACT_NUMBER')} *`}
          name="contractNumber"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.contractNumber}
          placeholder={`${t('ADD_CONTRACT.ENTER_CONTRACT_NUMBER')} *`}
          instruction={`${t('ADD_CONTRACT.CONTRACT_NUMBER_DESC')}`}
          error={submitted ? formik.errors.contractNumber : undefined}
          autoComplete='off'
        />
        <div className='address-inputs-wrapper'>
          <div className='address-pair-inputs-holder'>
            <CustomInput
              inputRef={fieldRefs.zip}
              containerStyle={{ width: '63px', flexShrink: 0 }}
              inputStyle={{ paddingLeft: '8px', paddingRight: '8px' }}
              label={`${t('ADD_CONTRACT.ZIP')} *`}
              name="zip"
              type="text"
              onChange={formik.handleChange}
              value={formik.values.zip}
              error={submitted ? formik.errors.zip : undefined}
              autoComplete='postal-code'
              placeholder={`${t('ADD_CONTRACT.ENTER_ZIP_CODE')} *`}
            />
            <CustomInput
              inputRef={fieldRefs.city}
              containerStyle={{ width: '100%' }}
              inputStyle={{ paddingLeft: '8px', paddingRight: '8px' }}
              label={`${t('ADD_CONTRACT.CITY')} *`}
              name="city"
              type="text"
              onChange={formik.handleChange}
              value={formik.values.city}
              error={submitted ? formik.errors.city : undefined}
              autoComplete='address-level2'
              placeholder={`${t('ADD_CONTRACT.ENTER_CITY')} *`}
            />
          </div>
          <div className='address-pair-inputs-holder'>
            <CustomInput
              inputRef={fieldRefs.street}
              containerStyle={{ width: '100%' }}
              inputStyle={{ paddingLeft: '8px', paddingRight: '8px' }}
              label={`${t('ADD_CONTRACT.STREET')} *`}
              name="street"
              type="text"
              onChange={formik.handleChange}
              value={formik.values.street}
              error={submitted ? formik.errors.street : undefined}
              autoComplete='street-address'
              placeholder={`${t('ADD_CONTRACT.ENTER_STREET')} *`}
            />
            <CustomInput
              inputRef={fieldRefs.streetNumber}
              containerStyle={{ width: '63px', flexShrink: 0 }}
              inputStyle={{ paddingLeft: '8px', paddingRight: '8px' }}
              label={`${t('ADD_CONTRACT.STREET_NUMBER')} *`}
              name="streetNumber"
              type="text"
              onChange={formik.handleChange}
              value={formik.values.streetNumber}
              error={submitted ? formik.errors.streetNumber : undefined}
              autoComplete='address-line2'
              placeholder={`${t('ADD_CONTRACT.ENTER_STREET_NUMBER')} *`}
            />
          </div>

        </div>
        <div className='buttons-holder center'>
          <Button
            className='main-btn'
            variant='contained'
            size='large'
            type="button"
            onClick={() => {
              handleSubmitButtonState('primary')
              formik.handleSubmit()
            }}
            aria-label={String(errorAlertMsg ? t('BUTTON.TRY_AGAIN') : t('BUTTON.SUBMIT'))}
          >
            {errorAlertMsg ? t('BUTTON.TRY_AGAIN') : t('BUTTON.SUBMIT')}
          </Button>
          {errorAlertMsg && (
            <Button
              className='secondary-btn'
              variant='contained'
              size='large'
              type="button"
              onClick={() => {
                handleSubmitButtonState('secondary')
                formik.handleSubmit()
              }}
              aria-label={String(t('ADD_CONTRACT.SUBMIT_MANUAL_APPROVAL'))}
            >
              {t('ADD_CONTRACT.SUBMIT_MANUAL_APPROVAL')}
            </Button>
          )}
        </div>
      </form>
      <Snackbar open={showErrorAlert} autoHideDuration={6000} onClose={() => setShowErrorAlert(false)} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <Alert severity='error' sx={{ width: '100%' }} variant='filled' role='alert' aria-live='assertive'>
          {errorAlertMsg}
        </Alert>
      </Snackbar>
    </div>
  )
}
